import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createScopedKey,
  listScopedKeys,
  revokeScopedKey,
} from "../../api/hub/identity";
import { hubRequest, hubBody } from "../../api/hub/client";
import { Link } from "react-router";
import { useWorkDirectory } from "../tasks/useTasks";
import { usePlatformSettings } from "./usePlatformSettings";
import type { PlatformSettings, Role } from "../../api/hub/types";
import "./platform-settings.css";
import { NotificationRecovery } from "./NotificationRecovery";
const actions = {
  triage: "拆解一句话输入（等发话人确认）",
  fill: "补空字段、关联和标签",
  flag: "标记日期矛盾、停滞等问题",
  weekly_report: "生成周报",
  project_update: "生成项目进展和健康度",
  project_agent: "项目管家数据监测",
  okr_progress: "汇总 OKR 进展（告知并可撤销）",
  post_enrichment: "补充灵感摘要",
  approval_precheck: "审批材料预审（告知并可撤销）",
};
const DEFAULT_TIME_ZONE = "Asia/Shanghai";
const timeZones = (() => {
  const supported = typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone")
    : ["Asia/Shanghai", "Asia/Taipei", "UTC"];
  return [...new Set(["Asia/Shanghai", "UTC", ...supported])];
})();
export function PlatformSettingsPage() {
  const work = usePlatformSettings(),
    directory = useWorkDirectory(),
    [draft, setDraft] = useState<PlatformSettings>({}),
    [message, setMessage] = useState("");
  useEffect(() => {
    if (work.settings.data) setDraft(work.settings.data);
  }, [work.settings.data]);
  const set = (key: string, value: unknown) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const notification = draft.notifications ?? {},
    ai = draft.ai ?? {};
  const selectedTimeZone = String(draft.timeZone || DEFAULT_TIME_ZONE);
  return (
    <section className="work-page settings-platform">
      <header className="work-heading">
        <div>
          <p className="work-eyebrow">偏好 · 中台设置</p>
          <h1>按你的节奏协作</h1>
        </div>
        <div>
          {directory.me.data?.role === "CEO" && <><Link to="/settings/platform/home">角色默认主页</Link>{" · "}</>}
          <Link to="/settings/platform/company-rules">公司规矩</Link>{" · "}<Link to="/settings">账号、模型与 API Key</Link>
        </div>
      </header>
      {work.settings.isPending ? (
        <p>正在加载设置…</p>
      ) : (
        <form
          className="work-form"
          onSubmit={(e) => {
            e.preventDefault();
            work.save.mutate(draft, {
              onSuccess: () => setMessage("设置已保存"),
              onError: (e) => setMessage(e.message),
            });
          }}
        >
          <fieldset>
            <legend>通知与免打扰</legend>
            <label className="setting-check">
              <input
                type="checkbox"
                checked={draft.notificationsEnabled !== false}
                onChange={(e) => set("notificationsEnabled", e.target.checked)}
              />
              接收普通通知（紧急提醒仍遵守系统规则）
            </label>
            <div className="work-form-grid">
              {[
                ["focusTime", "每日汇总（周一含周报与 OKR）", "09:00"],
              ].map(([key, label, defaultValue]) => (
                <label key={key}>
                  {label}
                  <input
                    type="time"
                    value={
                      (notification as Record<string, string>)[key] ??
                      defaultValue
                    }
                    onChange={(e) =>
                      set("notifications", {
                        ...notification,
                        [key]: e.target.value,
                      })
                    }
                  />
                </label>
              ))}
              <label>
                免打扰开始
                <input
                  type="time"
                  value={String(draft.dndStart ?? "22:00")}
                  onChange={(e) => set("dndStart", e.target.value)}
                />
              </label>
              <label>
                免打扰结束
                <input
                  type="time"
                  value={String(draft.dndEnd ?? "08:00")}
                  onChange={(e) => set("dndEnd", e.target.value)}
                />
              </label>
              <label>
                时区
                <select
                  value={selectedTimeZone}
                  onChange={(e) => set("timeZone", e.target.value)}
                >
                  {!timeZones.includes(selectedTimeZone) && <option value={selectedTimeZone}>{selectedTimeZone}</option>}
                  {timeZones.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
                </select>
              </label>
            </div>
            <label className="setting-check">
              <input
                type="checkbox"
                checked={draft.weekendsMuted !== false}
                onChange={(e) => set("weekendsMuted", e.target.checked)}
              />
              周末免打扰
            </label>
          </fieldset>
          <fieldset>
            <legend>AI 自动化</legend>
            <p className="work-meta">
              直接做：只补空、加关联、生成汇总。做了再告诉且可撤销：OKR 进展、审批预审。改已有值、合并、关闭先问人。审批同意或驳回、删除、外发、花钱、改目标或权限、替人完成或验收永远不做。确定性提醒与同步不受 AI 开关影响。
            </p>
            <label className="setting-check">
              <input
                type="checkbox"
                checked={draft.aiEnabled !== false && ai.enabled !== false}
                onChange={(e) => {
                  set("aiEnabled", e.target.checked);
                  set("ai", { ...ai, enabled: e.target.checked });
                }}
              />
              AI 总开关
            </label>
            <label>
              执行方式
              <select
                value={ai.mode ?? "automatic"}
                onChange={(e) => set("ai", { ...ai, mode: e.target.value })}
              >
                <option value="automatic">自动执行允许的动作</option>
                <option value="suggestion">只建议，由我确认</option>
              </select>
            </label>
            <div className="settings-actions">
              {Object.entries(actions).map(([key, title]) => (
                <label key={key} className="setting-check">
                  <input
                    type="checkbox"
                    disabled={draft.aiEnabled === false || ai.enabled === false}
                    checked={ai.actions?.[key] !== false}
                    onChange={(e) =>
                      set("ai", {
                        ...ai,
                        actions: { ...ai.actions, [key]: e.target.checked },
                      })
                    }
                  />
                  {title}
                </label>
              ))}
            </div>
          </fieldset>
          <button className="work-primary" disabled={work.save.isPending}>
            保存偏好
          </button>
        </form>
      )}
      {(message || work.settings.error) && (
        <p role="status">{message || work.settings.error?.message}</p>
      )}
      <fieldset>
        <legend>角色与人员</legend>
        <p className="work-meta">角色对应由 CEO 修改。其他成员可查看当前角色。</p>
        {directory.people.data?.map((p) => (
          <label className="settings-role" key={p.id}>
            {p.name}
            <select
              aria-label={`${p.name}的角色`}
              value={p.role}
              disabled={
                directory.me.data?.role !== "CEO" || work.role.isPending
              }
              onChange={(e) =>
                work.role.mutate(
                  { id: p.id, role: e.target.value as Role },
                  {
                    onError: (e) => setMessage(e.message),
                    onSuccess: () => setMessage("角色已更新"),
                  },
                )
              }
            >
              {["CEO", "CPO", "CTO", "member"].map((r) => (
                <option key={r} value={r}>
                  {r === "member" ? "普通成员" : r}
                </option>
              ))}
            </select>
          </label>
        ))}
      </fieldset>
      <fieldset>
        <legend>飞书绑定</legend>
        <p>
          {directory.me.data?.unionId ? "已绑定飞书账号" : "尚未绑定飞书账号"}
        </p>
        <a href="/hub/feishu/login">
          {directory.me.data?.unionId ? "重新通过飞书登录" : "绑定飞书账号"}
        </a>
      </fieldset>
      <NotificationRecovery actor={directory.me.data} />
      <GlobalAiSettings role={directory.me.data?.role} />
      <ScopedKeys />
    </section>
  );
}

function ScopedKeys() {
  const qc = useQueryClient(),
    [name, setName] = useState("浏览器摘录插件"),
    [secret, setSecret] = useState(""),
    [error, setError] = useState("");
  const keys = useQuery({ queryKey: ["hub", "keys"], queryFn: listScopedKeys });
  const create = useMutation({
    mutationFn: () => createScopedKey(name),
    onSuccess: (r) => {
      setSecret(r.key ?? "");
      void qc.invalidateQueries({ queryKey: ["hub", "keys"] });
    },
    onError: (e) => setError(e.message),
  });
  const revoke = useMutation({
    mutationFn: revokeScopedKey,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["hub", "keys"] }),
    onError: (e) => setError(e.message),
  });
  return (
    <fieldset>
      <legend>摘录插件专用 Key</legend>
      <p className="work-meta">
        只允许发布到灵感墙，不能读取资料或修改任务。完整 Key 只显示一次。
      </p>
      <div className="work-toolbar">
        <input
          aria-label="专用 Key 名称"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          disabled={!name.trim() || create.isPending || !!secret}
          onClick={() => create.mutate()}
        >
          创建专用 Key
        </button>
      </div>
      {secret && (
        <div>
          <label className="work-form">
            新 Key（请存入插件）
            <input readOnly aria-label="一次性专用 Key" value={secret} />
          </label>
          <div className="work-toolbar">
            <button
              onClick={() =>
                void navigator.clipboard
                  .writeText(secret)
                  .catch(() => setError("复制未完成，请手动复制"))
              }
            >
              复制 Key
            </button>
            <button onClick={() => setSecret("")}>已保存，隐藏 Key</button>
          </div>
        </div>
      )}
      {(error || keys.error) && (
        <p role="alert">{error || keys.error?.message}</p>
      )}
      {keys.data?.map((k) => (
        <p key={k.id}>
          {k.name} · {k.revokedAt ? "已吊销" : "灵感墙投稿与人员目录"}
          <button
            disabled={!!k.revokedAt || revoke.isPending}
            onClick={() => revoke.mutate(k.id)}
          >
            吊销 {k.name}
          </button>
        </p>
      ))}
    </fieldset>
  );
}

type GlobalPreferences = {
  enabled: boolean;
  suggestionsOnly: boolean;
  actions: Record<string, boolean>;
};
function GlobalAiSettings({ role }: { role?: Role }) {
  const qc = useQueryClient(),
    query = useQuery({
      queryKey: ["hub", "ai-settings"],
      queryFn: () => hubRequest<GlobalPreferences>("/ai/settings"),
    }),
    [draft, setDraft] = useState<GlobalPreferences>({
      enabled: true,
      suggestionsOnly: false,
      actions: {},
    }),
    [message, setMessage] = useState("");
  useEffect(() => {
    if (query.data) setDraft(query.data);
  }, [query.data]);
  const canEdit = !!role && ["CEO", "CPO", "CTO"].includes(role);
  const save = useMutation({
    mutationFn: () =>
      hubRequest<GlobalPreferences>("/ai/settings", hubBody(draft, "PUT")),
    onSuccess: () => {
      setMessage("全局 AI 开关已保存");
      void qc.invalidateQueries({ queryKey: ["hub", "ai-settings"] });
    },
    onError: (e) => setMessage(e.message),
  });
  return (
    <fieldset>
      <legend>全员 AI 规则</legend>
      <p className="work-meta">
        全局规则与个人偏好同时生效；任何一层关闭后，该动作只给建议。管理员可以修改全局规则。
      </p>
      <details>
        <summary>查看和调整全局 AI 开关</summary>
        {query.isPending ? (
          <p>正在加载全局规则…</p>
        ) : (
          <form
            className="work-form"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <label className="setting-check">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={draft.enabled}
                onChange={(e) =>
                  setDraft((s) => ({ ...s, enabled: e.target.checked }))
                }
              />
              全局 AI 总开关
            </label>
            <label>
              全局执行方式
              <select
                disabled={!canEdit}
                value={draft.suggestionsOnly ? "suggestion" : "automatic"}
                onChange={(e) =>
                  setDraft((s) => ({
                    ...s,
                    suggestionsOnly: e.target.value === "suggestion",
                  }))
                }
              >
                <option value="automatic">自动执行允许的动作</option>
                <option value="suggestion">只建议，由人确认</option>
              </select>
            </label>
            <div className="settings-actions">
              {Object.entries(actions).map(([key, title]) => (
                <label key={key} className="setting-check">
                  <input
                    type="checkbox"
                    disabled={!canEdit || !draft.enabled}
                    checked={draft.actions[key] !== false}
                    onChange={(e) =>
                      setDraft((s) => ({
                        ...s,
                        actions: { ...s.actions, [key]: e.target.checked },
                      }))
                    }
                  />
                  {title}（全局）
                </label>
              ))}
            </div>
            {canEdit && (
              <button disabled={save.isPending} className="work-primary">
                保存全局 AI 开关
              </button>
            )}
          </form>
        )}
        {(message || query.error) && (
          <p role="status">{message || query.error?.message}</p>
        )}
      </details>
    </fieldset>
  );
}
